import { Button, Form, Input, Select, Space } from "antd";
import type { ComponentProps, ComponentType } from "react";
import { useEffect } from "react";
import {
    BaseItem as ZhBaseItem,
    Dialog as ZhDialog,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import { OIL_SITE_TYPE } from "@zhihaoscm/meta/src/oilSite";
import { OIL_TYPE, TIME_PERIOD } from "@zhihaoscm/meta/src/oilOrder";
import { fmtMoney } from "@/utils/money";
import type { OilOrderPlanForm, OilOrderRow } from "./OilOrderPage";

// beta.13 运行时兼容旧 MoneyInput 的 controls 配置，类型声明暂未暴露该字段。
type OilMoneyInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ZhMoneyInput = ZhInputNumber as ComponentType<OilMoneyInputProps>;

/** 报计划日期沿用旧 Vue：只能选择当天及之后的日期。 */
const isBeforeToday = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
};

export interface OilOrderPlanDialogProps {
    open: boolean;
    row?: OilOrderRow;
    onCancel: () => void;
    onSubmit: (form: OilOrderPlanForm) => Promise<void>;
}

interface OilSiteSummary {
    address?: string;
    phone?: string;
    type?: number;
}

/** Vue 报计划弹窗的 React 迁移版，沿用 planInfo 嵌套 payload。 */
export function OilOrderPlanDialog({ open, row, onCancel, onSubmit }: OilOrderPlanDialogProps) {
    const [form] = Form.useForm<OilOrderPlanForm>();
    const planInfo = Form.useWatch<OilOrderPlanForm["planInfo"]>("planInfo", form);
    const site = row?.oilSite as OilSiteSummary | undefined;
    // 预估油费与 Vue 版保持一致，使用当前表单中的油价乘以加油量。
    const refuelingCost = (planInfo?.refuelingPrice || 0) * (planInfo?.refuelingVolume || 0);

    // 通过 antd Form.submit 触发校验，只有校验成功后才把完整嵌套 payload 交给父页面。
    const handleSubmit = () => {
        form.submit();
    };

    useEffect(() => {
        if (!row) return;
        form.setFieldsValue({
            oilSiteId: row.oilSite?.id || "",
            planInfo: {
                oilType: row.oilOrder.oilType === undefined ? undefined : Number(row.oilOrder.oilType),
                refuelingVolume: row.oilOrder.bookingRefuelingVolume,
                refuelingTime: row.oilOrder.bookingRefuelingTime,
                refuelingTimePeriod: row.oilOrder.bookingRefuelingTimePeriod,
                refuelingPrice: row.oilOrder.bookingRefuelingPrice
            }
        });
    }, [form, row]);

    return (
        <ZhDialog
            open={open}
            title="报计划"
            width={800}
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={handleSubmit}>
                        确认
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={values => void onSubmit(values)}>
                <ZhGrid cols={2} gap={[16, 0]}>
                    <ZhGridItem span={2}>
                        <Form.Item label="站点" required htmlFor="oilSiteId">
                            <div className="input-info-wrap">
                                <Form.Item
                                    noStyle
                                    name="oilSiteId"
                                    rules={[{ required: true, message: "请选择预约站点" }]}
                                >
                                    <Input placeholder="请选择预约站点" />
                                </Form.Item>
                                <div className="info-wrap">
                                    <ZhBaseItem label="站点地址" value={site?.address || "-"} />
                                    <ZhBaseItem
                                        label="站点类型"
                                        value={OIL_SITE_TYPE.getIntroByCode(site?.type) || "-"}
                                    />
                                    <ZhBaseItem label="站点联系方式" value={site?.phone || "-"} />
                                </div>
                            </div>
                        </Form.Item>
                    </ZhGridItem>
                    <Form.Item
                        label="油品类型"
                        name={["planInfo", "oilType"]}
                        rules={[{ required: true, message: "请选择油品类型" }]}
                    >
                        <Select
                            options={OIL_TYPE.options.map(item => ({ value: item.value, label: item.label }))}
                            placeholder="请选择油品类型"
                        />
                    </Form.Item>
                    <Form.Item
                        label="油价"
                        name={["planInfo", "refuelingPrice"]}
                        rules={[
                            { required: true, message: "请输入油价" },
                            { type: "number", min: 0.01, max: 999999.99 }
                        ]}
                    >
                        <ZhMoneyInput controls={false} precision={2} suffix="元/吨" />
                    </Form.Item>
                    <Form.Item
                        label="加油量"
                        name={["planInfo", "refuelingVolume"]}
                        rules={[
                            { required: true, message: "请输入加油量" },
                            { type: "number", min: 0.01, max: 9999.99 }
                        ]}
                    >
                        <ZhMoneyInput controls={false} precision={2} suffix="吨" />
                    </Form.Item>
                    <Form.Item label="预估油费">{fmtMoney(refuelingCost, "元")}</Form.Item>
                    <ZhGridItem span={2}>
                        <Form.Item
                            label="计划加油时间"
                            name={["planInfo", "refuelingTime"]}
                            rules={[{ required: true, message: "请选择计划加油时间" }]}
                        >
                            <ZhDatePicker
                                aria-label="计划加油时间"
                                className="w-full"
                                disabledDate={isBeforeToday}
                                format="YYYY-MM-DD"
                                placeholder="请选择计划加油时间"
                                type="date"
                                valueFormat="YYYY-MM-DD"
                                onChange={() => {
                                    // 日期变化后清空旧时间段，避免继续提交与新日期不匹配的时段。
                                    form.setFieldValue(["planInfo", "refuelingTimePeriod"], undefined);
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="时间段"
                            name={["planInfo", "refuelingTimePeriod"]}
                            rules={[{ required: true, message: "请选择时间段" }]}
                        >
                            <Select
                                options={TIME_PERIOD.options.map(item => ({ value: item.value, label: item.label }))}
                            />
                        </Form.Item>
                    </ZhGridItem>
                </ZhGrid>
            </Form>
        </ZhDialog>
    );
}

export default OilOrderPlanDialog;
